import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Textarea } from '@/components/ui/textarea';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, ScatterChart, Scatter, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { TrendingUp, BarChart3, PieChart as PieChartIcon, Activity, Target, Radar as RadarIcon, Download, FileText, FileImage, Table, FileCode } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useDownloadGate } from '@/components/DownloadGate/useDownloadGate';

interface RiskAssessment {
  id: string;
  user_id: string;
  risk_name: string;
  risk_category: string;
  likelihood: number;
  impact: number;
  risk_score: number;
  status: string;
  created_at: string;
}

export const RiskAnalyticsDashboard = () => {
  const { toast } = useToast();
  const { guardDownload } = useDownloadGate();
  const [risks, setRisks] = useState<RiskAssessment[]>([]);
  const [chartType, setChartType] = useState('bar');
  const [analysisType, setAnalysisType] = useState('category');
  const [timeframe, setTimeframe] = useState('all');
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const chartRef = useRef<HTMLDivElement>(null);
  
  // Create refs for each category outside the map function
  const climateChartRef = useRef<HTMLDivElement>(null);
  const regulatoryChartRef = useRef<HTMLDivElement>(null);
  const operationalChartRef = useRef<HTMLDivElement>(null);
  const financialChartRef = useRef<HTMLDivElement>(null);
  const reputationalChartRef = useRef<HTMLDivElement>(null);

  // State for individual category controls
  const [categoryChartTypes, setCategoryChartTypes] = useState({
    climate: 'bar',
    regulatory: 'bar',
    operational: 'bar',
    financial: 'bar',
    reputational: 'bar'
  });

  const [categoryAnalysisTypes, setCategoryAnalysisTypes] = useState({
    climate: 'severity',
    regulatory: 'severity',
    operational: 'severity',
    financial: 'severity',
    reputational: 'severity'
  });

  const [categoryTimeframes, setCategoryTimeframes] = useState({
    climate: 'all',
    regulatory: 'all',
    operational: 'all',
    financial: 'all',
    reputational: 'all'
  });

  const [categoryStartDates, setCategoryStartDates] = useState({
    climate: undefined as Date | undefined,
    regulatory: undefined as Date | undefined,
    operational: undefined as Date | undefined,
    financial: undefined as Date | undefined,
    reputational: undefined as Date | undefined
  });

  const [categoryEndDates, setCategoryEndDates] = useState({
    climate: undefined as Date | undefined,
    regulatory: undefined as Date | undefined,
    operational: undefined as Date | undefined,
    financial: undefined as Date | undefined,
    reputational: undefined as Date | undefined
  });

  useEffect(() => {
    fetchRisks();
  }, []);

  const fetchRisks = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch risks from Supabase
      const { data, error } = await supabase
        .from('risk_assessments')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Process risks to calculate risk_score
      const processedRisks = (data || []).map((risk: any) => ({
        ...risk,
        risk_score: risk.likelihood * risk.impact
      }));

      setRisks(processedRisks);
      generateInsights(processedRisks);
      generateRecommendations(processedRisks);
    } catch (error: any) {
      console.error('Error fetching risks from Supabase:', error);
      toast({
        title: "Error",
        description: "Failed to fetch risk data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const generateInsights = (riskData: RiskAssessment[]) => {
    const totalRisks = riskData.length;
    const highRisks = riskData.filter(r => r.risk_score >= 15).length;
    const avgScore = totalRisks > 0 ? riskData.reduce((sum, risk) => sum + risk.risk_score, 0) / totalRisks : 0;
    const mostCommonCategory = getMostCommon(riskData.map(r => r.risk_category));
    const activeRisks = riskData.filter(r => r.status === 'active').length;

    const insightText = `
Comprehensive Risk Analysis Summary
==================================

📊 Key Metrics:
• Total risks assessed: ${totalRisks}
• High-risk items (score ≥15): ${highRisks} (${totalRisks > 0 ? ((highRisks/totalRisks)*100).toFixed(1) : 0}%)
• Average risk score: ${avgScore.toFixed(1)}
• Most common risk category: ${mostCommonCategory}
• Active risks requiring attention: ${activeRisks}

🔍 Key Findings:
${highRisks > totalRisks * 0.3 ? '⚠️  Consider immediate action for high-risk items to reduce overall exposure' : '✅ Risk profile appears manageable with current mitigation strategies'}
${activeRisks > totalRisks * 0.7 ? '⚠️  High number of active risks - prioritize mitigation efforts' : '✅ Good progress on risk mitigation activities'}
• Regular review and update of risk assessments recommended
• Focus on ${mostCommonCategory} risks as they represent the highest frequency category
• Cross-category risk interdependencies should be monitored
    `.trim();

    setInsights(insightText);
  };

  const generateRecommendations = (riskData: RiskAssessment[]) => {
    const totalRisks = riskData.length;
    const highRisks = riskData.filter(r => r.risk_score >= 15).length;
    const avgScore = totalRisks > 0 ? riskData.reduce((sum, risk) => sum + risk.risk_score, 0) / totalRisks : 0;
    const mostCommonCategory = getMostCommon(riskData.map(r => r.risk_category));
    const activeRisks = riskData.filter(r => r.status === 'active').length;

    const recommendationText = `
Strategic Risk Management Recommendations
========================================

🚨 IMMEDIATE ACTIONS (Next 30 Days):
${highRisks > 0 ? `• Address ${highRisks} high-risk items identified in the assessment` : '• No immediate high-risk items requiring urgent attention'}
• Establish risk monitoring dashboard for real-time tracking
• Assign risk owners for all active risks
• Implement cross-category risk alert systems

📈 SHORT-TERM INITIATIVES (Next 90 Days):
• Develop comprehensive mitigation strategies for ${mostCommonCategory} risks
• Implement quarterly risk review process across all categories
• Establish risk communication protocols and escalation procedures
• Create risk response playbooks for each category
• Conduct risk management training for key stakeholders

🎯 LONG-TERM STRATEGY (Next 12 Months):
• Build risk-aware organizational culture
• Invest in risk management training and advanced tools
• Develop business continuity plans for critical risks
• Establish enterprise-wide risk governance framework
• Create comprehensive risk performance metrics and KPIs

🔄 CONTINUOUS IMPROVEMENT:
• Regular risk assessment updates (quarterly recommended)
• Performance metrics tracking and reporting
• Stakeholder engagement and communication
• Industry benchmarking and best practice adoption
• Regular review and update of risk management strategies
    `.trim();

    setRecommendations(recommendationText);
  };

  const getMostCommon = (arr: string[]) => {
    const frequency: { [key: string]: number } = {};
    arr.forEach(item => frequency[item] = (frequency[item] || 0) + 1);
    return Object.keys(frequency).reduce((a, b) => frequency[a] > frequency[b] ? a : b, '');
  };

  const getFilteredData = () => {
    let filtered = risks;

    if (filterCategory !== 'all') {
      filtered = filtered.filter(risk => risk.risk_category === filterCategory);
    }

    if (timeframe !== 'all' && startDate && endDate) {
      filtered = filtered.filter(risk => {
        const riskDate = new Date(risk.created_at);
        return riskDate >= startDate && riskDate <= endDate;
      });
    }

    return filtered;
  };

  const getChartData = () => {
    const filteredRisks = getFilteredData();

    switch (analysisType) {
      case 'category':
        return getCategoryData(filteredRisks);
      case 'severity':
        return getSeverityData(filteredRisks);
      case 'status':
        return getStatusData(filteredRisks);
      case 'timeline':
        return getTimelineData(filteredRisks);
      default:
        return getCategoryData(filteredRisks);
    }
  };

  const getCategoryData = (data: RiskAssessment[]) => {
    const categories = ['climate', 'regulatory', 'operational', 'financial', 'reputational'];
    return categories.map(category => ({
      name: category.charAt(0).toUpperCase() + category.slice(1),
      count: data.filter(r => r.risk_category === category).length,
      avgScore: data.filter(r => r.risk_category === category).length > 0 
        ? data.filter(r => r.risk_category === category).reduce((sum, r) => sum + r.risk_score, 0) / data.filter(r => r.risk_category === category).length 
        : 0
    }));
  };

  const getSeverityData = (data: RiskAssessment[]) => {
    return [
      { name: 'Low (1-9)', count: data.filter(r => r.risk_score < 10).length, color: '#22c55e' },
      { name: 'Medium (10-14)', count: data.filter(r => r.risk_score >= 10 && r.risk_score < 15).length, color: '#eab308' },
      { name: 'High (15-19)', count: data.filter(r => r.risk_score >= 15 && r.risk_score < 20).length, color: '#f97316' },
      { name: 'Critical (20+)', count: data.filter(r => r.risk_score >= 20).length, color: '#ef4444' }
    ];
  };

  const getStatusData = (data: RiskAssessment[]) => {
    const statuses = ['active', 'mitigated', 'closed'];
    return statuses.map(status => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      count: data.filter(r => r.status === status).length
    }));
  };

  const getTimelineData = (data: RiskAssessment[]) => {
    const timelineData: { [key: string]: number } = {};
    data.forEach(risk => {
      const month = new Date(risk.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      timelineData[month] = (timelineData[month] || 0) + 1;
    });
    return Object.entries(timelineData).map(([month, count]) => ({ month, count }));
  };

  const renderChart = () => {
    const data = getChartData();
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

    switch (chartType) {
      case 'bar':
        return (
          <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="count" fill="#8884d8" />
            {analysisType === 'category' && <Bar dataKey="avgScore" fill="#82ca9d" />}
          </BarChart>
        );
      case 'pie':
        return (
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, value }) => `${name}: ${value}`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="count"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        );
      case 'line':
        return (
          <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={analysisType === 'timeline' ? 'month' : 'name'} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="count" stroke="#8884d8" />
          </LineChart>
        );
      case 'scatter':
        return (
          <ScatterChart data={getFilteredData()} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <CartesianGrid />
            <XAxis type="number" dataKey="likelihood" name="Likelihood" unit="" />
            <YAxis type="number" dataKey="impact" name="Impact" unit="" />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
            <Scatter name="Risks" data={getFilteredData()} fill="#8884d8" />
          </ScatterChart>
        );
      case 'radar':
        const radarData = getCategoryData(getFilteredData()).map(item => ({
          category: item.name,
          score: item.avgScore
        }));
        return (
          <RadarChart data={radarData}>
            <PolarGrid />
            <PolarAngleAxis dataKey="category" />
            <PolarRadiusAxis angle={18} domain={[0, 25]} />
            <Radar name="Average Risk Score" dataKey="score" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
            <Tooltip />
          </RadarChart>
        );
      default:
        return <div>Chart type not supported</div>;
    }
  };

  // Download functions (same as before, no changes needed)
  const downloadAsImage = async () => {
    if (!chartRef.current) return;
    
    try {
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
      });
      
      const link = document.createElement('a');
      link.download = `risk-analytics-${analysisType}-${chartType}.png`;
      link.href = canvas.toDataURL();
      link.click();
      
      toast({
        title: "Success",
        description: "Chart downloaded as PNG successfully",
      });
    } catch (error) {
      console.error('Error downloading chart as image:', error);
      toast({
        title: "Error",
        description: "Failed to download chart as image",
        variant: "destructive",
      });
    }
  };

  const downloadAsCSV = async () => {
    if (!(await guardDownload())) return;

    const data = getChartData();
    if (!data || data.length === 0) return;
    
    try {
      const headers = Object.keys(data[0] || {});
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(header => row[header]).join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const link = document.createElement('a');
      link.download = `risk-analytics-${analysisType}-${chartType}.csv`;
      link.href = URL.createObjectURL(blob);
      link.click();
      URL.revokeObjectURL(link.href);
      
      toast({
        title: "Success",
        description: "Data downloaded as CSV successfully",
      });
    } catch (error) {
      console.error('Error downloading chart data as CSV:', error);
      toast({
        title: "Error",
        description: "Failed to download data as CSV",
        variant: "destructive",
      });
    }
  };

  const downloadAsHTML = async () => {
    if (!(await guardDownload())) return;

    const data = getChartData();
    if (!data || data.length === 0) return;
    
    try {
      const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Risk Analytics Report</title>
    <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 20px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
        .container { max-width: 1200px; margin: 0 auto; background: white; border-radius: 15px; box-shadow: 0 20px 40px rgba(0,0,0,0.1); overflow: hidden; }
        .header { background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%); color: white; padding: 40px; text-align: center; }
        .header h1 { margin: 0; font-size: 2.5em; font-weight: 300; }
        .header p { margin: 10px 0 0 0; opacity: 0.9; font-size: 1.1em; }
        .content { padding: 40px; }
        .metrics-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin-bottom: 40px; }
        .metric-card { background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%); padding: 20px; border-radius: 10px; text-align: center; border-left: 4px solid #22c55e; }
        .metric-value { font-size: 2em; font-weight: bold; color: #22c55e; }
        .metric-label { color: #64748b; margin-top: 5px; }
        .data-table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        .data-table th, .data-table td { padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
        .data-table th { background: #f8fafc; font-weight: 600; color: #374151; }
        .data-table tr:hover { background: #f1f5f9; }
        .footer { background: #f8fafc; padding: 20px; text-align: center; color: #64748b; border-top: 1px solid #e2e8f0; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Risk Analytics Report</h1>
            <p>Comprehensive analysis of organizational risk assessment data</p>
        </div>
        <div class="content">
            <div class="metrics-grid">
                <div class="metric-card">
                    <div class="metric-value">${risks.length}</div>
                    <div class="metric-label">Total Risks</div>
                </div>
                <div class="metric-card">
                    <div class="metric-value">${risks.filter(r => r.risk_score >= 15).length}</div>
                    <div class="metric-label">High Risk Items</div>
                </div>
                <div class="metric-card">
                    <div class="metric-value">${risks.length > 0 ? (risks.reduce((sum, risk) => sum + risk.risk_score, 0) / risks.length).toFixed(1) : 0}</div>
                    <div class="metric-label">Average Risk Score</div>
                </div>
                <div class="metric-card">
                    <div class="metric-value">${risks.filter(r => r.status === 'active').length}</div>
                    <div class="metric-label">Active Risks</div>
                </div>
            </div>
            <h2>Analysis Results</h2>
            <table class="data-table">
                <thead>
                    <tr>
                        ${Object.keys(data[0] || {}).map(key => `<th>${key.charAt(0).toUpperCase() + key.slice(1)}</th>`).join('')}
                    </tr>
                </thead>
                <tbody>
                    ${data.map(row => `
                        <tr>
                            ${Object.values(row).map(value => `<td>${value}</td>`).join('')}
                        </tr>
                    `).join('')}
                </tbody>
            </table>
        </div>
        <div class="footer">
            <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
        </div>
    </div>
</body>
</html>`;
      
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const link = document.createElement('a');
      link.download = `risk-analytics-${analysisType}-${chartType}.html`;
      link.href = URL.createObjectURL(blob);
      link.click();
      URL.revokeObjectURL(link.href);
      
      toast({
        title: "Success",
        description: "Report downloaded as HTML successfully",
      });
    } catch (error) {
      console.error('Error downloading as HTML:', error);
      toast({
        title: "Error",
        description: "Failed to download as HTML",
        variant: "destructive",
      });
    }
  };

  const downloadProfessionalPDF = async () => {
    if (!(await guardDownload())) return;

    if (!chartRef.current) return;
    
    try {
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Set document properties
      pdf.setProperties({
        title: 'Risk Analytics Dashboard Report',
        subject: 'Comprehensive Risk Analysis Report',
        author: 'Greencard Sustainability Manager',
        creator: 'Greencard Sustainability Manager'
      });

      let yPosition = 20;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      const contentWidth = pageWidth - (2 * margin);

      // Modern Header Design
      pdf.setFillColor(34, 197, 94); // Green background
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      // Logo/Title area
      pdf.setFontSize(20);
      pdf.setTextColor(255, 255, 255);
      pdf.setFont(undefined, 'bold');
      pdf.text('Greencard', margin, 25);
      
      // Subtitle
      pdf.setFontSize(14);
      pdf.setTextColor(255, 255, 255);
      pdf.setFont(undefined, 'normal');
      pdf.text('Risk Analytics Dashboard Report', margin + 45, 25);
      
      // Date
      pdf.setFontSize(10);
      pdf.setTextColor(255, 255, 255);
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, margin + 45, 32);
      
      yPosition = 50;

      // Executive Summary Section with modern styling
      pdf.setFillColor(248, 250, 252); // Light gray background
      pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 25, 'F');
      
      pdf.setFontSize(16);
      pdf.setTextColor(34, 197, 94);
      pdf.setFont(undefined, 'bold');
      pdf.text('Executive Summary', margin, yPosition + 8);
      pdf.setTextColor(0, 0, 0);
      
      yPosition += 20;
      pdf.setFontSize(10);
      const totalRisks = risks.length;
      const highRisks = risks.filter(r => r.risk_score >= 15).length;
      const avgScore = totalRisks > 0 ? risks.reduce((sum, risk) => sum + risk.risk_score, 0) / totalRisks : 0;
      
      const summaryText = [
        `This comprehensive risk analysis report provides a detailed assessment of ${totalRisks} identified risks across the organization.`,
        `The analysis reveals ${highRisks} high-risk items requiring immediate attention, with an average risk score of ${avgScore.toFixed(1)}.`,
        `Key findings indicate that ${getMostCommon(risks.map(r => r.risk_category))} risks represent the most prevalent category,`,
        `necessitating focused mitigation strategies and enhanced monitoring protocols.`
      ];

      summaryText.forEach(line => {
        const lines = pdf.splitTextToSize(line, contentWidth);
        lines.forEach(textLine => {
          if (yPosition > 250) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.text(textLine, margin, yPosition);
          yPosition += 5;
        });
        yPosition += 2;
      });

      yPosition += 15;

      // Key Metrics Section with modern cards
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 35, 'F');
      
      pdf.setFontSize(16);
      pdf.setTextColor(34, 197, 94);
      pdf.setFont(undefined, 'bold');
      pdf.text('Key Risk Metrics', margin, yPosition + 8);
      pdf.setTextColor(0, 0, 0);
      
      yPosition += 15;
      pdf.setFontSize(10);
      
      const metrics = [
        { label: 'Total Risks Assessed', value: totalRisks.toString() },
        { label: 'High-Risk Items (Score ≥15)', value: `${highRisks} (${totalRisks > 0 ? ((highRisks/totalRisks)*100).toFixed(1) : 0}%)` },
        { label: 'Average Risk Score', value: avgScore.toFixed(1) },
        { label: 'Most Common Category', value: getMostCommon(risks.map(r => r.risk_category)) },
        { label: 'Active Risks', value: risks.filter(r => r.status === 'active').length.toString() }
      ];

      metrics.forEach(metric => {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(34, 197, 94);
        pdf.text(`${metric.label}:`, margin, yPosition);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(0, 0, 0);
        pdf.text(metric.value, margin + 60, yPosition);
        yPosition += 6;
      });

      yPosition += 15;

      // Chart Representation Section
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 25, 'F');
      
      pdf.setFontSize(16);
      pdf.setTextColor(34, 197, 94);
      pdf.setFont(undefined, 'bold');
      pdf.text('Chart Representation', margin, yPosition + 8);
      pdf.setTextColor(0, 0, 0);
      
      yPosition += 15;
      pdf.setFontSize(10);
      pdf.text(`Analysis Type: ${analysisType.charAt(0).toUpperCase() + analysisType.slice(1)}`, margin, yPosition);
      yPosition += 5;
      pdf.text(`Chart Type: ${chartType.charAt(0).toUpperCase() + chartType.slice(1)} Chart`, margin, yPosition);
      
      yPosition += 10;

      // Add chart image
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      if (yPosition + imgHeight > 250) {
        pdf.addPage();
        yPosition = 20;
      }
      
      pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
      yPosition += imgHeight + 15;

      // Analysis Insights Section
      if (insights) {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        
        pdf.setFillColor(248, 250, 252);
        pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 25, 'F');
        
        pdf.setFontSize(16);
        pdf.setTextColor(34, 197, 94);
        pdf.setFont(undefined, 'bold');
        pdf.text('Analysis Insights', margin, yPosition + 8);
        pdf.setTextColor(0, 0, 0);
        
        yPosition += 15;
        pdf.setFontSize(10);
        
        const insightLines = insights.split('\n');
        insightLines.forEach(line => {
          if (yPosition > 250) {
            pdf.addPage();
            yPosition = 20;
          }
          
          if (line.trim().startsWith('•')) {
            pdf.setFont(undefined, 'bold');
            pdf.setTextColor(34, 197, 94);
            pdf.text(line.trim(), margin, yPosition);
            pdf.setTextColor(0, 0, 0);
            pdf.setFont(undefined, 'normal');
          } else if (line.trim().startsWith('Analysis Insights:') || line.trim().startsWith('Key Findings:')) {
            pdf.setFont(undefined, 'bold');
            pdf.text(line.trim(), margin, yPosition);
            pdf.setFont(undefined, 'normal');
          } else {
            pdf.text(line.trim(), margin, yPosition);
          }
          yPosition += 5;
        });
        
        yPosition += 10;
      }

      // Strategic Recommendations Section
      if (recommendations) {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        
        pdf.setFillColor(248, 250, 252);
        pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 25, 'F');
        
        pdf.setFontSize(16);
        pdf.setTextColor(34, 197, 94);
        pdf.setFont(undefined, 'bold');
        pdf.text('Strategic Recommendations', margin, yPosition + 8);
        pdf.setTextColor(0, 0, 0);
        
        yPosition += 15;
        pdf.setFontSize(10);
        
        const recommendationLines = recommendations.split('\n');
        recommendationLines.forEach(line => {
          if (yPosition > 250) {
            pdf.addPage();
            yPosition = 20;
          }
          
          if (line.trim().startsWith('1.') || line.trim().startsWith('2.') || line.trim().startsWith('3.') || line.trim().startsWith('4.')) {
            pdf.setFont(undefined, 'bold');
            pdf.setTextColor(34, 197, 94);
            pdf.text(line.trim(), margin, yPosition);
            pdf.setTextColor(0, 0, 0);
            pdf.setFont(undefined, 'normal');
          } else if (line.trim().startsWith('•')) {
            pdf.text(line.trim(), margin + 5, yPosition);
          } else if (line.trim().startsWith('Strategic Recommendations:')) {
            pdf.setFont(undefined, 'bold');
            pdf.text(line.trim(), margin, yPosition);
            pdf.setFont(undefined, 'normal');
          } else {
            pdf.text(line.trim(), margin, yPosition);
          }
          yPosition += 5;
        });
      }

      // Modern footer
      const pageCount = pdf.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFillColor(34, 197, 94);
        pdf.rect(0, 280, pageWidth, 20, 'F');
        pdf.setFontSize(8);
        pdf.setTextColor(255, 255, 255);
        pdf.text(`Page ${i} of ${pageCount}`, pageWidth - 30, 290);
        pdf.text('Greencard Sustainability Manager - Risk Analytics Report', margin, 290);
        pdf.setTextColor(0, 0, 0);
      }

      // Download the PDF
      const filename = `risk-analytics-report-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);
      
      toast({
        title: "Success",
        description: "Professional PDF report downloaded successfully",
      });
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Error",
        description: "Failed to generate PDF report",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading analytics...</div>;
  }

  const riskCategories = ['climate', 'regulatory', 'operational', 'financial', 'reputational'];

  return (
    <div className="space-y-8">
      {/* Combined Analytics Section */}
      <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-gray-50">
        <CardHeader className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-t-lg">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-3 text-2xl font-light">
                <div className="p-2 bg-white/20 rounded-lg">
                  <BarChart3 className="h-6 w-6" />
                </div>
                Combined Risk Analytics Dashboard
              </CardTitle>
              <p className="text-green-100 mt-2 text-sm">
                Comprehensive risk analysis across all categories
              </p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-white/20 hover:bg-white/30 border-white/30 text-white backdrop-blur-sm"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <DropdownMenuItem onClick={downloadProfessionalPDF}>
                  <FileText className="h-4 w-4 mr-2" />
                  Professional PDF Report
                </DropdownMenuItem>
                <DropdownMenuItem onClick={downloadAsImage}>
                  <FileImage className="h-4 w-4 mr-2" />
                  Download as PNG
                </DropdownMenuItem>
                <DropdownMenuItem onClick={downloadAsCSV}>
                  <Table className="h-4 w-4 mr-2" />
                  Download as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={downloadAsHTML}>
                  <FileCode className="h-4 w-4 mr-2" />
                  Download as HTML
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent className="space-y-6 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Chart Type</label>
              <Select value={chartType} onValueChange={setChartType}>
                <SelectTrigger className="bg-white border-gray-200 hover:border-green-300 focus:border-green-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Bar Chart
                    </div>
                  </SelectItem>
                  <SelectItem value="pie">
                    <div className="flex items-center gap-2">
                      <PieChartIcon className="h-4 w-4" />
                      Pie Chart
                    </div>
                  </SelectItem>
                  <SelectItem value="line">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Line Chart
                    </div>
                  </SelectItem>
                  <SelectItem value="scatter">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      Scatter Plot
                    </div>
                  </SelectItem>
                  <SelectItem value="radar">
                    <div className="flex items-center gap-2">
                      <RadarIcon className="h-4 w-4" />
                      Radar Chart
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Analysis Type</label>
              <Select value={analysisType} onValueChange={setAnalysisType}>
                <SelectTrigger className="bg-white border-gray-200 hover:border-green-300 focus:border-green-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="category">By Category</SelectItem>
                  <SelectItem value="severity">By Severity</SelectItem>
                  <SelectItem value="status">By Status</SelectItem>
                  <SelectItem value="timeline">Timeline</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Risk Category</label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="bg-white border-gray-200 hover:border-green-300 focus:border-green-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="climate">Climate</SelectItem>
                  <SelectItem value="regulatory">Regulatory</SelectItem>
                  <SelectItem value="operational">Operational</SelectItem>
                  <SelectItem value="financial">Financial</SelectItem>
                  <SelectItem value="reputational">Reputational</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Timeframe</label>
              <Select value={timeframe} onValueChange={setTimeframe}>
                <SelectTrigger className="bg-white border-gray-200 hover:border-green-300 focus:border-green-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {timeframe === 'custom' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Start Date</label>
                <DatePicker date={startDate} onDateChange={setStartDate} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">End Date</label>
                <DatePicker date={endDate} onDateChange={setEndDate} />
              </div>
            </div>
          )}

          <div ref={chartRef} className="h-96 bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
            <ResponsiveContainer width="100%" height="100%">
              {renderChart()}
            </ResponsiveContainer>
          </div>

          {insights && (
            <Card className="border-0 shadow-md bg-gradient-to-r from-blue-50 to-indigo-50 hidden">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-blue-800 text-lg">
                  <Activity className="h-5 w-5" />
                  Analysis Insights
                </CardTitle>
                <p className="text-blue-600 text-sm">
                  Comprehensive analysis of risk patterns and trends across all categories
                </p>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="bg-white/80 rounded-lg border border-blue-200 p-4">
                  <pre className="font-mono text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                    {insights}
                  </pre>
                </div>
              </CardContent>
            </Card>
          )}

          {recommendations && (
            <Card className="border-0 shadow-md bg-gradient-to-r from-green-50 to-emerald-50 hidden">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-green-800 text-lg">
                  <Target className="h-5 w-5" />
                  Strategic Recommendations
                </CardTitle>
                <p className="text-green-600 text-sm">
                  Actionable strategies for comprehensive risk management and mitigation
                </p>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="bg-white/80 rounded-lg border border-green-200 p-4">
                  <pre className="font-mono text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                    {recommendations}
                  </pre>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      {/* Individual Risk Category Sections */}
      {riskCategories.map((category) => {
        const categoryRisks = risks.filter(risk => risk.risk_category === category);
        
        // Get the appropriate ref for this category
        const getCategoryRef = () => {
          switch (category) {
            case 'climate': return climateChartRef;
            case 'regulatory': return regulatoryChartRef;
            case 'operational': return operationalChartRef;
            case 'financial': return financialChartRef;
            case 'reputational': return reputationalChartRef;
            default: return climateChartRef;
          }
        };
        
        const categoryRef = getCategoryRef();
        
        // Get category-specific state
        const categoryChartType = categoryChartTypes[category as keyof typeof categoryChartTypes];
        const categoryAnalysisType = categoryAnalysisTypes[category as keyof typeof categoryAnalysisTypes];
        const categoryTimeframe = categoryTimeframes[category as keyof typeof categoryTimeframes];
        const categoryStartDate = categoryStartDates[category as keyof typeof categoryStartDates];
        const categoryEndDate = categoryEndDates[category as keyof typeof categoryEndDates];

        // Filter category risks based on timeframe
        const getFilteredCategoryRisks = () => {
          let filtered = categoryRisks;

          if (categoryTimeframe !== 'all' && categoryStartDate && categoryEndDate) {
            filtered = filtered.filter(risk => {
              const riskDate = new Date(risk.created_at);
              return riskDate >= categoryStartDate && riskDate <= categoryEndDate;
            });
          }

          return filtered;
        };
        
        const getCategoryChartData = () => {
          const filteredRisks = getFilteredCategoryRisks();
          
          switch (categoryAnalysisType) {
            case 'severity':
              return [
                { name: 'Low (1-9)', count: filteredRisks.filter(r => r.risk_score < 10).length, color: '#22c55e' },
                { name: 'Medium (10-14)', count: filteredRisks.filter(r => r.risk_score >= 10 && r.risk_score < 15).length, color: '#eab308' },
                { name: 'High (15-19)', count: filteredRisks.filter(r => r.risk_score >= 15 && r.risk_score < 20).length, color: '#f97316' },
                { name: 'Critical (20+)', count: filteredRisks.filter(r => r.risk_score >= 20).length, color: '#ef4444' }
              ];
            case 'status':
              const statuses = ['active', 'mitigated', 'closed'];
              return statuses.map(status => ({
                name: status.charAt(0).toUpperCase() + status.slice(1),
                count: filteredRisks.filter(r => r.status === status).length
              }));
            case 'timeline':
              const timelineData: { [key: string]: number } = {};
              filteredRisks.forEach(risk => {
                const month = new Date(risk.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
                timelineData[month] = (timelineData[month] || 0) + 1;
              });
              return Object.entries(timelineData).map(([month, count]) => ({ month, count }));
            default:
              return [{ name: 'Total', count: filteredRisks.length }];
          }
        };

        const renderCategoryChart = () => {
          const data = getCategoryChartData();
          const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

          switch (categoryChartType) {
            case 'bar':
              return (
                <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#8884d8" />
                </BarChart>
              );
            case 'pie':
              return (
                <PieChart>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              );
            case 'line':
              return (
                <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey={categoryAnalysisType === 'timeline' ? 'month' : 'name'} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" stroke="#8884d8" />
                </LineChart>
              );
            case 'scatter':
              return (
                <ScatterChart data={getFilteredCategoryRisks()} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                  <CartesianGrid />
                  <XAxis type="number" dataKey="likelihood" name="Likelihood" unit="" />
                  <YAxis type="number" dataKey="impact" name="Impact" unit="" />
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                  <Scatter name="Risks" data={getFilteredCategoryRisks()} fill="#8884d8" />
                </ScatterChart>
              );
            case 'radar':
              const radarData = [
                { category: 'Total Risks', score: getFilteredCategoryRisks().length },
                { category: 'High Risk', score: getFilteredCategoryRisks().filter(r => r.risk_score >= 15).length },
                { category: 'Active', score: getFilteredCategoryRisks().filter(r => r.status === 'active').length },
                { category: 'Avg Score', score: getFilteredCategoryRisks().length > 0 ? getFilteredCategoryRisks().reduce((sum, r) => sum + r.risk_score, 0) / getFilteredCategoryRisks().length : 0 }
              ];
              return (
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="category" />
                  <PolarRadiusAxis angle={18} domain={[0, Math.max(...radarData.map(d => d.score)) + 5]} />
                  <Radar name="Metrics" dataKey="score" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                  <Tooltip />
                </RadarChart>
              );
            default:
              return <div>Chart type not supported</div>;
          }
        };

        const generateCategoryInsights = () => {
          const filteredRisks = getFilteredCategoryRisks();
          const totalRisks = filteredRisks.length;
          const highRisks = filteredRisks.filter(r => r.risk_score >= 15).length;
          const avgScore = totalRisks > 0 ? filteredRisks.reduce((sum, risk) => sum + risk.risk_score, 0) / totalRisks : 0;
          const activeRisks = filteredRisks.filter(r => r.status === 'active').length;

          return `
${category.charAt(0).toUpperCase() + category.slice(1)} Risk Analysis Summary
===============================================

📊 Key Metrics:
• Total ${category} risks identified: ${totalRisks}
• High-risk items (score ≥15): ${highRisks} (${totalRisks > 0 ? ((highRisks/totalRisks)*100).toFixed(1) : 0}%)
• Average risk score: ${avgScore.toFixed(1)}
• Active risks requiring attention: ${activeRisks}

🔍 Key Findings:
${highRisks > totalRisks * 0.3 ? '⚠️  High concentration of critical risks requiring immediate attention' : '✅ Risk profile appears manageable within this category'}
${activeRisks > totalRisks * 0.7 ? '⚠️  High number of active risks - prioritize mitigation efforts' : '✅ Good progress on risk mitigation in this category'}
• Regular monitoring and review recommended for ${category} risks
• Focus on ${category}-specific risk patterns and trends
          `.trim();
        };

        const generateCategoryRecommendations = () => {
          const filteredRisks = getFilteredCategoryRisks();
          const totalRisks = filteredRisks.length;
          const highRisks = filteredRisks.filter(r => r.risk_score >= 15).length;
          const avgScore = totalRisks > 0 ? filteredRisks.reduce((sum, risk) => sum + risk.risk_score, 0) / totalRisks : 0;

          return `
${category.charAt(0).toUpperCase() + category.slice(1)} Risk Strategic Recommendations
========================================================

🚨 IMMEDIATE ACTIONS (Next 30 Days):
${highRisks > 0 ? `• Address ${highRisks} high-risk ${category} items identified in the assessment` : '• No immediate high-risk items requiring urgent attention in this category'}
• Establish ${category}-specific monitoring protocols and dashboards
• Assign specialized risk owners for ${category} risks
• Implement ${category} risk alert systems

📈 SHORT-TERM INITIATIVES (Next 90 Days):
• Develop comprehensive ${category}-specific mitigation strategies
• Implement category-focused risk review process (monthly recommended)
• Establish ${category} risk communication protocols
• Create ${category} risk response playbooks
• Conduct ${category} risk training for relevant teams

🎯 LONG-TERM STRATEGY (Next 12 Months):
• Build ${category} risk expertise within organization
• Invest in ${category}-specific risk management tools and technologies
• Develop ${category} risk response plans and business continuity measures
• Establish ${category} risk governance framework
• Create ${category} risk performance metrics and KPIs

🔄 CONTINUOUS IMPROVEMENT:
• Regular ${category} risk assessment updates (quarterly recommended)
• ${category}-specific performance metrics tracking and reporting
• Stakeholder engagement and communication for ${category} risks
• Industry benchmarking and best practice adoption
• Regular review and update of ${category} risk strategies
          `.trim();
        };

        const downloadCategoryPDF = async () => {
          if (!(await guardDownload())) return;

          if (!categoryRef.current) return;
          
          try {
            const canvas = await html2canvas(categoryRef.current, {
              backgroundColor: '#ffffff',
              scale: 2,
            });

            const pdf = new jsPDF('p', 'mm', 'a4');
            
            pdf.setProperties({
              title: `${category.charAt(0).toUpperCase() + category.slice(1)} Risk Analytics Report`,
              subject: `${category.charAt(0).toUpperCase() + category.slice(1)} Risk Analysis Report`,
              author: 'Greencard Sustainability Manager',
              creator: 'Greencard Sustainability Manager'
            });

            let yPosition = 20;
            const pageWidth = pdf.internal.pageSize.getWidth();
            const margin = 20;
            const contentWidth = pageWidth - (2 * margin);

            // Modern Header Design
            pdf.setFillColor(34, 197, 94);
            pdf.rect(0, 0, pageWidth, 40, 'F');
            
            pdf.setFontSize(20);
            pdf.setTextColor(255, 255, 255);
            pdf.setFont(undefined, 'bold');
            pdf.text('Greencard', margin, 25);
            
            pdf.setFontSize(14);
            pdf.setTextColor(255, 255, 255);
            pdf.setFont(undefined, 'normal');
            pdf.text(`${category.charAt(0).toUpperCase() + category.slice(1)} Risk Analytics Report`, margin + 45, 25);
            
            pdf.setFontSize(10);
            pdf.setTextColor(255, 255, 255);
            pdf.text(`Generated: ${new Date().toLocaleDateString()}`, margin + 45, 32);
            
            yPosition = 50;

            // Executive Summary
            pdf.setFillColor(248, 250, 252);
            pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 25, 'F');
            
            pdf.setFontSize(16);
            pdf.setTextColor(34, 197, 94);
            pdf.setFont(undefined, 'bold');
            pdf.text('Executive Summary', margin, yPosition + 8);
            pdf.setTextColor(0, 0, 0);
            
            yPosition += 20;
            pdf.setFontSize(10);
            
            const filteredRisks = getFilteredCategoryRisks();
            const totalRisks = filteredRisks.length;
            const highRisks = filteredRisks.filter(r => r.risk_score >= 15).length;
            const avgScore = totalRisks > 0 ? filteredRisks.reduce((sum, risk) => sum + risk.risk_score, 0) / totalRisks : 0;
            
            const summaryText = [
              `This ${category} risk analysis report provides a detailed assessment of ${totalRisks} identified ${category} risks.`,
              `The analysis reveals ${highRisks} high-risk items requiring immediate attention, with an average risk score of ${avgScore.toFixed(1)}.`,
              `Key findings indicate specific patterns and trends within the ${category} risk category,`,
              `necessitating focused mitigation strategies and enhanced monitoring protocols.`
            ];

            summaryText.forEach(line => {
              const lines = pdf.splitTextToSize(line, contentWidth);
              lines.forEach(textLine => {
                if (yPosition > 250) {
                  pdf.addPage();
                  yPosition = 20;
                }
                pdf.text(textLine, margin, yPosition);
                yPosition += 5;
              });
              yPosition += 2;
            });

            yPosition += 15;

            // Key Metrics
            pdf.setFillColor(248, 250, 252);
            pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 35, 'F');
            
            pdf.setFontSize(16);
            pdf.setTextColor(34, 197, 94);
            pdf.setFont(undefined, 'bold');
            pdf.text('Key Metrics', margin, yPosition + 8);
            pdf.setTextColor(0, 0, 0);
            
            yPosition += 15;
            pdf.setFontSize(10);
            
            const metrics = [
              { label: 'Total Risks', value: totalRisks.toString() },
              { label: 'High-Risk Items', value: `${highRisks} (${totalRisks > 0 ? ((highRisks/totalRisks)*100).toFixed(1) : 0}%)` },
              { label: 'Average Risk Score', value: avgScore.toFixed(1) },
              { label: 'Active Risks', value: filteredRisks.filter(r => r.status === 'active').length.toString() }
            ];

            metrics.forEach(metric => {
              if (yPosition > 250) {
                pdf.addPage();
                yPosition = 20;
              }
              pdf.setFont(undefined, 'bold');
              pdf.setTextColor(34, 197, 94);
              pdf.text(`${metric.label}:`, margin, yPosition);
              pdf.setFont(undefined, 'normal');
              pdf.setTextColor(0, 0, 0);
              pdf.text(metric.value, margin + 60, yPosition);
              yPosition += 6;
            });

            yPosition += 15;

            // Chart
            pdf.setFillColor(248, 250, 252);
            pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 25, 'F');
            
            pdf.setFontSize(16);
            pdf.setTextColor(34, 197, 94);
            pdf.setFont(undefined, 'bold');
            pdf.text('Chart Representation', margin, yPosition + 8);
            pdf.setTextColor(0, 0, 0);
            
            yPosition += 15;
            pdf.setFontSize(10);
            pdf.text(`Analysis Type: ${categoryAnalysisType.charAt(0).toUpperCase() + categoryAnalysisType.slice(1)}`, margin, yPosition);
            yPosition += 5;
            pdf.text(`Chart Type: ${categoryChartType.charAt(0).toUpperCase() + categoryChartType.slice(1)} Chart`, margin, yPosition);
            
            yPosition += 10;

            const imgData = canvas.toDataURL('image/png');
            const imgWidth = contentWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            
            if (yPosition + imgHeight > 250) {
              pdf.addPage();
              yPosition = 20;
            }
            
            pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 15;

            // Analysis Insights
            const categoryInsights = generateCategoryInsights();
            if (categoryInsights) {
              if (yPosition > 250) {
                pdf.addPage();
                yPosition = 20;
              }
              
              pdf.setFillColor(248, 250, 252);
              pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 25, 'F');
              
              pdf.setFontSize(16);
              pdf.setTextColor(34, 197, 94);
              pdf.setFont(undefined, 'bold');
              pdf.text('Analysis Insights', margin, yPosition + 8);
              pdf.setTextColor(0, 0, 0);
              
              yPosition += 15;
              pdf.setFontSize(10);
              
              const insightLines = categoryInsights.split('\n');
              insightLines.forEach(line => {
                if (yPosition > 250) {
                  pdf.addPage();
                  yPosition = 20;
                }
                
                if (line.trim().startsWith('•')) {
                  pdf.setFont(undefined, 'bold');
                  pdf.setTextColor(34, 197, 94);
                  pdf.text(line.trim(), margin, yPosition);
                  pdf.setTextColor(0, 0, 0);
                  pdf.setFont(undefined, 'normal');
                } else if (line.trim().startsWith(`${category.charAt(0).toUpperCase() + category.slice(1)} Risk Analysis Summary`) || line.trim().startsWith('Key Findings:')) {
                  pdf.setFont(undefined, 'bold');
                  pdf.text(line.trim(), margin, yPosition);
                  pdf.setFont(undefined, 'normal');
                } else {
                  pdf.text(line.trim(), margin, yPosition);
                }
                yPosition += 5;
              });
              
              yPosition += 10;
            }

            // Recommendations
            const categoryRecommendations = generateCategoryRecommendations();
            if (categoryRecommendations) {
              if (yPosition > 250) {
                pdf.addPage();
                yPosition = 20;
              }
              
              pdf.setFillColor(248, 250, 252);
              pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 25, 'F');
              
              pdf.setFontSize(16);
              pdf.setTextColor(34, 197, 94);
              pdf.setFont(undefined, 'bold');
              pdf.text('Strategic Recommendations', margin, yPosition + 8);
              pdf.setTextColor(0, 0, 0);
              
              yPosition += 15;
              pdf.setFontSize(10);
              
              const recommendationLines = categoryRecommendations.split('\n');
              recommendationLines.forEach(line => {
                if (yPosition > 250) {
                  pdf.addPage();
                  yPosition = 20;
                }
                
                if (line.trim().startsWith('1.') || line.trim().startsWith('2.') || line.trim().startsWith('3.') || line.trim().startsWith('4.')) {
                  pdf.setFont(undefined, 'bold');
                  pdf.setTextColor(34, 197, 94);
                  pdf.text(line.trim(), margin, yPosition);
                  pdf.setTextColor(0, 0, 0);
                  pdf.setFont(undefined, 'normal');
                } else if (line.trim().startsWith('•')) {
                  pdf.text(line.trim(), margin + 5, yPosition);
                } else if (line.trim().startsWith(`${category.charAt(0).toUpperCase() + category.slice(1)} Risk Strategic Recommendations`)) {
                  pdf.setFont(undefined, 'bold');
                  pdf.text(line.trim(), margin, yPosition);
                  pdf.setFont(undefined, 'normal');
                } else {
                  pdf.text(line.trim(), margin, yPosition);
                }
                yPosition += 5;
              });
            }

            // Footer
            const pageCount = pdf.internal.pages.length - 1;
            for (let i = 1; i <= pageCount; i++) {
              pdf.setPage(i);
              pdf.setFillColor(34, 197, 94);
              pdf.rect(0, 280, pageWidth, 20, 'F');
              pdf.setFontSize(8);
              pdf.setTextColor(255, 255, 255);
              pdf.text(`Page ${i} of ${pageCount}`, pageWidth - 30, 290);
              pdf.text(`Greencard - ${category.charAt(0).toUpperCase() + category.slice(1)} Risk Analytics Report`, margin, 290);
              pdf.setTextColor(0, 0, 0);
            }

            const filename = `${category}-risk-analytics-report-${new Date().toISOString().split('T')[0]}.pdf`;
            pdf.save(filename);
            
            toast({
              title: "Success",
              description: `${category.charAt(0).toUpperCase() + category.slice(1)} risk PDF report downloaded successfully`,
            });
            
          } catch (error) {
            console.error('Error generating PDF:', error);
            toast({
              title: "Error",
              description: `Failed to generate ${category} risk PDF report`,
              variant: "destructive",
            });
          }
        };

        const downloadCategoryImage = async () => {
          if (!categoryRef.current) return;
          
          try {
            const canvas = await html2canvas(categoryRef.current, {
              backgroundColor: '#ffffff',
              scale: 2,
            });
            
            const link = document.createElement('a');
            link.download = `${category}-risk-analytics-${categoryAnalysisType}-${categoryChartType}.png`;
            link.href = canvas.toDataURL();
            link.click();
            
            toast({
              title: "Success",
              description: `${category.charAt(0).toUpperCase() + category.slice(1)} risk chart downloaded as PNG successfully`,
            });
          } catch (error) {
            console.error('Error downloading chart as image:', error);
            toast({
              title: "Error",
              description: `Failed to download ${category} risk chart as image`,
              variant: "destructive",
            });
          }
        };

        const downloadCategoryCSV = async () => {
          if (!(await guardDownload())) return;

          const data = getCategoryChartData();
          if (!data || data.length === 0) return;
          
          try {
            const headers = Object.keys(data[0] || {});
            const csvContent = [
              headers.join(','),
              ...data.map(row => headers.map(header => row[header]).join(','))
            ].join('\n');
            
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const link = document.createElement('a');
            link.download = `${category}-risk-analytics-${categoryAnalysisType}-${categoryChartType}.csv`;
            link.href = URL.createObjectURL(blob);
            link.click();
            URL.revokeObjectURL(link.href);
            
            toast({
              title: "Success",
              description: `${category.charAt(0).toUpperCase() + category.slice(1)} risk data downloaded as CSV successfully`,
            });
          } catch (error) {
            console.error('Error downloading chart data as CSV:', error);
            toast({
              title: "Error",
              description: `Failed to download ${category} risk data as CSV`,
              variant: "destructive",
            });
          }
        };

        const categoryInsights = generateCategoryInsights();
        const categoryRecommendations = generateCategoryRecommendations();

        return (
          <Card key={category} className="border-0 shadow-lg bg-gradient-to-br from-white to-gray-50">
            <CardHeader className={`bg-gradient-to-r ${getCategoryGradient(category)} text-white rounded-t-lg`}>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-3 text-2xl font-light">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <BarChart3 className="h-6 w-6" />
                    </div>
                    {category.charAt(0).toUpperCase() + category.slice(1)} Risk Analytics
                  </CardTitle>
                  <p className="text-white/80 mt-2 text-sm">
                    Detailed analysis of {category} risk category
                  </p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="bg-white/20 hover:bg-white/30 border-white/30 text-white backdrop-blur-sm"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56">
                    <DropdownMenuItem onClick={downloadCategoryPDF}>
                      <FileText className="h-4 w-4 mr-2" />
                      Professional PDF Report
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={downloadCategoryImage}>
                      <FileImage className="h-4 w-4 mr-2" />
                      Download as PNG
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={downloadCategoryCSV}>
                      <Table className="h-4 w-4 mr-2" />
                      Download as CSV
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 p-6">
              {/* Category-specific filter controls */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Chart Type</label>
                  <Select 
                    value={categoryChartType} 
                    onValueChange={(value) => setCategoryChartTypes(prev => ({ ...prev, [category]: value }))}
                  >
                    <SelectTrigger className="bg-white border-gray-200 hover:border-green-300 focus:border-green-500">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bar">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="h-4 w-4" />
                          Bar Chart
                        </div>
                      </SelectItem>
                      <SelectItem value="pie">
                        <div className="flex items-center gap-2">
                          <PieChartIcon className="h-4 w-4" />
                          Pie Chart
                        </div>
                      </SelectItem>
                      <SelectItem value="line">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4" />
                          Line Chart
                        </div>
                      </SelectItem>
                      <SelectItem value="scatter">
                        <div className="flex items-center gap-2">
                          <Target className="h-4 w-4" />
                          Scatter Plot
                        </div>
                      </SelectItem>
                      <SelectItem value="radar">
                        <div className="flex items-center gap-2">
                          <RadarIcon className="h-4 w-4" />
                          Radar Chart
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Analysis Type</label>
                  <Select 
                    value={categoryAnalysisType} 
                    onValueChange={(value) => setCategoryAnalysisTypes(prev => ({ ...prev, [category]: value }))}
                  >
                    <SelectTrigger className="bg-white border-gray-200 hover:border-green-300 focus:border-green-500">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="severity">By Severity</SelectItem>
                      <SelectItem value="status">By Status</SelectItem>
                      <SelectItem value="timeline">Timeline</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Timeframe</label>
                  <Select 
                    value={categoryTimeframe} 
                    onValueChange={(value) => setCategoryTimeframes(prev => ({ ...prev, [category]: value }))}
                  >
                    <SelectTrigger className="bg-white border-gray-200 hover:border-green-300 focus:border-green-500">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Time</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Risk Count</label>
                  <div className="p-2 bg-gray-50 rounded-md text-center">
                    <span className="text-lg font-semibold text-gray-700">{getFilteredCategoryRisks().length}</span>
                    <span className="text-sm text-gray-500 ml-1">risks</span>
                  </div>
                </div>
              </div>

              {categoryTimeframe === 'custom' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Start Date</label>
                    <DatePicker 
                      date={categoryStartDate} 
                      onDateChange={(date) => setCategoryStartDates(prev => ({ ...prev, [category]: date }))} 
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">End Date</label>
                    <DatePicker 
                      date={categoryEndDate} 
                      onDateChange={(date) => setCategoryEndDates(prev => ({ ...prev, [category]: date }))} 
                    />
                  </div>
                </div>
              )}

              <div ref={categoryRef} className="h-96 bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                <ResponsiveContainer width="100%" height="100%">
                  {renderCategoryChart()}
                </ResponsiveContainer>
              </div>

              {categoryInsights && (
                <Card className="border-0 shadow-md bg-gradient-to-r from-blue-50 to-indigo-50 hidden">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-blue-800 text-lg">
                      <Activity className="h-5 w-5" />
                      {category.charAt(0).toUpperCase() + category.slice(1)} Analysis Insights
                    </CardTitle>
                    <p className="text-blue-600 text-sm">
                      Comprehensive analysis of {category} risk patterns and trends
                    </p>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="bg-white/80 rounded-lg border border-blue-200 p-4">
                      <pre className="font-mono text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                        {categoryInsights}
                      </pre>
                    </div>
                  </CardContent>
                </Card>
              )}

              {categoryRecommendations && (
                <Card className="border-0 shadow-md bg-gradient-to-r from-green-50 to-emerald-50 hidden">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-green-800 text-lg">
                      <Target className="h-5 w-5" />
                      {category.charAt(0).toUpperCase() + category.slice(1)} Strategic Recommendations
                    </CardTitle>
                    <p className="text-green-600 text-sm">
                      Actionable strategies for {category} risk management and mitigation
                    </p>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="bg-white/80 rounded-lg border border-green-200 p-4">
                      <pre className="font-mono text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                        {categoryRecommendations}
                      </pre>
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// Helper function to get category-specific gradients
const getCategoryGradient = (category: string) => {
  switch (category) {
    case 'climate':
      return 'from-blue-500 to-cyan-600';
    case 'regulatory':
      return 'from-purple-500 to-indigo-600';
    case 'operational':
      return 'from-orange-500 to-red-600';
    case 'financial':
      return 'from-emerald-500 to-teal-600';
    case 'reputational':
      return 'from-pink-500 to-rose-600';
    default:
      return 'from-green-500 to-green-600';
  }
};
